import { labelOf } from "@carry/shared";
import type { z } from "zod";
import type {
  DailyReportAlcoholResult,
  DailyReportStatus,
  DailyReportWeather,
} from "./driver-daily-report";
import type { ExpensePaymentMethod, ExpenseType } from "./driver-expense";
import type { LineSendStatus } from "./line-stats";
import type { NotificationType } from "./notification";
import type { RouteStatus } from "./route";

// ────────────────────────────────────────────────────────
// contract で定義した enum の表示ラベル (単一ソース)。
//
// 画面ごとに `Record<string, string>` のローカル対応表を作ると、enum に値が増えても型が漏れを
// 検出できず、`?? 生値` のフォールバックで英語の内部値が無言のまま画面に出る (#210 / #564 / #739)。
// ラベルはここだけで定義し、admin / driver / CSV / 通知から共用する。
//
// - マップは必ず `Record<Enum, string>` で宣言する (値追加時に型エラーで落とす)。
// - 表示関数は未知値でも落とさず、生値を含む形で返す (画面を壊さず調査可能にする)。
// - shared で定義した enum (TimeSlot / OrderStatus / DeliveryStatus など) のラベルは
//   `@carry/shared` の labels.ts が正典。ここでは再定義しない。
// ────────────────────────────────────────────────────────

/**
 * 運転日報ステータスの表示ラベル (driver 視点)。
 * ドライバーには「自分の日報が今どう扱われているか」を見せる。draft は勤怠・配送実績から
 * システムが自動生成した状態なので「自動生成済」と呼ぶ。
 */
export const DAILY_REPORT_STATUS_LABELS_FOR_DRIVER: Record<
  z.infer<typeof DailyReportStatus>,
  string
> = {
  draft: "自動生成済",
  submitted: "確認済",
  approved: "承認済",
  rejected: "差戻し",
};

/**
 * 運転日報ステータスの表示ラベル (admin 視点)。
 * 管理側には承認業務の状態として見せる (submitted = 自分がまだ承認していない)。
 * driver 視点と語彙が違うのは意図的だが、定義はこの 1 ファイルに閉じる。
 */
export const DAILY_REPORT_STATUS_LABELS_FOR_ADMIN: Record<
  z.infer<typeof DailyReportStatus>,
  string
> = {
  draft: "下書き",
  submitted: "未承認",
  approved: "承認済",
  rejected: "差戻し",
};

/** 運転日報ステータスを driver 向け表示に変換する。未知値はそのまま返す (fail-safe)。 */
export const dailyReportStatusLabelForDriver = (status: string): string =>
  labelOf(DAILY_REPORT_STATUS_LABELS_FOR_DRIVER, status);

/** 運転日報ステータスを admin 向け表示に変換する。未知値はそのまま返す (fail-safe)。 */
export const dailyReportStatusLabelForAdmin = (status: string): string =>
  labelOf(DAILY_REPORT_STATUS_LABELS_FOR_ADMIN, status);

/**
 * アルコール検査結果の表示ラベル。driver 履歴・admin 日報詳細・運転日報 CSV で共用する。
 * 通知タイトル (「アルコール検査 FAIL」) は通知種別ラベル側で別に持つ。
 */
export const DAILY_REPORT_ALCOHOL_RESULT_LABELS: Record<
  z.infer<typeof DailyReportAlcoholResult>,
  string
> = {
  pass: "合格",
  fail: "不合格",
};

/** アルコール検査結果を日本語表示に変換する。未記録 (null) の扱いは呼び出し側で決める。 */
export const dailyReportAlcoholResultLabel = (result: string): string =>
  labelOf(DAILY_REPORT_ALCOHOL_RESULT_LABELS, result);

/** 運転日報の天候の表示ラベル。 */
export const DAILY_REPORT_WEATHER_LABELS: Record<z.infer<typeof DailyReportWeather>, string> = {
  sunny: "晴れ",
  cloudy: "曇り",
  rainy: "雨",
  snowy: "雪",
  other: "その他",
};

/** 天候を日本語表示に変換する。未知値はそのまま返す (fail-safe)。 */
export const dailyReportWeatherLabel = (weather: string): string =>
  labelOf(DAILY_REPORT_WEATHER_LABELS, weather);

/**
 * 通知種別の表示ラベル。ヘッダー通知・通知一覧・LINE 送信ログで共用する。
 * 3 画面で表記がズレると同じ通知が別物に見えるため、短縮形は作らない。
 */
export const NOTIFICATION_TYPE_LABELS: Record<z.infer<typeof NotificationType>, string> = {
  delivery_assigned: "配送指示",
  delivery_unassigned: "割当解除",
  delivery_schedule_changed: "到着予定変更",
  instruction_cancelled: "配車確定解除",
  daily_report_approved: "日報承認",
  daily_report_rejected: "日報差戻し",
  clock_out_reminder: "退勤リマインド",
  distance_deviation_alert: "距離乖離アラート",
  delay_reported: "遅延報告",
  alcohol_check_failed: "アルコール検査 FAIL",
  vehicle_inspection_ng: "車両点検 NG",
  license_expiration_alert: "免許有効期限警告",
  news_published: "お知らせ",
};

/**
 * 通知種別を日本語表示に変換する。
 * 未知の種別は生値を含む「不明な通知（xxx）」にして、どの種別が未対応かを画面から追えるようにする。
 */
export const notificationTypeLabel = (type: string): string => {
  const label = (NOTIFICATION_TYPE_LABELS as Record<string, string | undefined>)[type];
  return label ?? `不明な通知（${type}）`;
};

/**
 * レーン (ルート) ステータスの表示ラベル。
 * admin の確定バッジ (確定前 / 確定) と用語を揃え、confirmed は「確定」と呼ぶ。
 * draft が抜けていると driver 側で生値が出るため、全ステータスを型で網羅させる。
 */
export const ROUTE_STATUS_LABELS: Record<z.infer<typeof RouteStatus>, string> = {
  draft: "下書き",
  assigned: "割当済",
  confirmed: "確定",
  started: "配送中",
  completed: "完了",
};

/** レーン (ルート) ステータスを日本語表示に変換する。未知値はそのまま返す (fail-safe)。 */
export const routeStatusLabel = (status: string): string => labelOf(ROUTE_STATUS_LABELS, status);

/** LINE Push 送信ステータスの表示ラベル (F22 LINE 送信ログ)。 */
export const LINE_SEND_STATUS_LABELS: Record<z.infer<typeof LineSendStatus>, string> = {
  pending: "送信待ち",
  sent: "送信済み",
  delivered: "配信済み",
  failed: "失敗",
};

/** LINE 送信ステータスを日本語表示に変換する。未知値はそのまま返す (fail-safe)。 */
export const lineSendStatusLabel = (status: string): string =>
  labelOf(LINE_SEND_STATUS_LABELS, status);

/** 経費種別の表示ラベル (driver 経費登録・admin 代理入力で共用)。 */
export const EXPENSE_TYPE_LABELS: Record<z.infer<typeof ExpenseType>, string> = {
  highway: "高速代",
  parking: "駐車場",
  other: "その他",
};

/** 経費種別を日本語表示に変換する。未知値はそのまま返す (fail-safe)。 */
export const expenseTypeLabel = (type: string): string => labelOf(EXPENSE_TYPE_LABELS, type);

/** 経費の支払方法の表示ラベル。 */
export const EXPENSE_PAYMENT_METHOD_LABELS: Record<z.infer<typeof ExpensePaymentMethod>, string> = {
  cash: "現金",
  credit_card: "クレジット",
  invoice: "請求書払",
  accounts_receivable: "売掛",
};

/** 支払方法を日本語表示に変換する。未知値はそのまま返す (fail-safe)。 */
export const expensePaymentMethodLabel = (method: string): string =>
  labelOf(EXPENSE_PAYMENT_METHOD_LABELS, method);
